import { type LucideIcon, Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button/button';

type PrimaryAction = {
  readonly label: string;
  readonly onClick: () => void;
  readonly icon?: LucideIcon;
};

type PageHeaderProps = {
  readonly title: string;
  readonly description?: ReactNode;
  readonly actions?: ReactNode;
  /** The page's main action: a button here on desktop, a floating button on phones. */
  readonly primaryAction?: PrimaryAction;
  /** Phones show the section in the top bar, so the title is hidden there unless it says more. */
  readonly titleOnPhone?: boolean;
};

export function PageHeader({ title, description, actions, primaryAction, titleOnPhone }: PageHeaderProps) {
  const Icon = primaryAction?.icon ?? Plus;
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div className="flex min-w-0 flex-col gap-1">
        <h1
          className={cn(
            'text-2xl font-semibold tracking-tight md:text-3xl',
            !titleOnPhone && 'max-md:sr-only',
          )}
        >
          {title}
        </h1>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {(actions || primaryAction) && (
        <div className="flex items-center gap-2">
          {actions}
          {primaryAction && (
            <>
              <Button size="lg" className="max-md:hidden" onClick={primaryAction.onClick}>
                <Icon /> {primaryAction.label}
              </Button>
              <Button
                size="icon-lg"
                aria-label={primaryAction.label}
                onClick={primaryAction.onClick}
                className="fixed right-4 bottom-[calc(5.25rem+env(safe-area-inset-bottom))] z-20 size-14 rounded-2xl shadow-lg md:hidden [&_svg:not([class*='size-'])]:size-6"
              >
                <Icon />
              </Button>
            </>
          )}
        </div>
      )}
    </header>
  );
}
